import {
  useRef,
  useState,
  useSyncExternalStore,
  type ComponentType,
  type ReactNode,
} from "react";
import {
  Checkbox,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  Progress,
  Spinner,
  Textarea,
  cn,
} from "@sfc/ui";
import { DownloadIcon, PaperclipIcon, UploadIcon, XIcon } from "lucide-react";
import type {
  AnswerValue,
  Choice,
  FileRef,
  Question,
  QuestionOption,
  QuestionType,
} from "../types";
import {
  declaredContentType,
  isAcceptedType,
  maxFileSizeMbOf,
  maxFilesOf,
  offersOther,
  optionsOf,
  ratingMax,
  reconcileAnswer,
  rowsOf,
} from "../registry";
import { FileUploadRefusal, useFileUploads } from "./fileUploads";
import { RATING_ICONS, fileUploadSummary } from "./registry";

/**
 * The live inputs an applicant answers with — the `Render` half the registry
 * next door reserves a slot for. One component per type, keyed statically so
 * a new type can't ship without one.
 *
 * Every component here is controlled and stateless over the answer: it reads
 * `value` and reports a whole `AnswerValue` (or `undefined` for "cleared")
 * through `onChange`. That's what lets the same components serve the preview
 * wizard now and a collaborative response document later — the storage of the
 * answer is the caller's business, never the control's.
 *
 * Blankness has one representation: a control whose answer is emptied reports
 * `undefined` rather than an empty value, so "unanswered" never has two
 * spellings for `isEmptyValue` and the visibility rules to disagree over.
 */
export interface QuestionRenderProps {
  question: Question;
  value: AnswerValue | undefined;
  onChange: (value: AnswerValue | undefined) => void;
  /** The id of the element labelling this question, for aria wiring. */
  labelId: string;
  /** Marks the controls invalid after a failed continue. */
  invalid?: boolean;
  /**
   * The seed shuffle-enabled questions order their options by. The *caller*
   * owns it, held for the whole fill session, because these controls unmount
   * whenever the applicant leaves the section — a seed kept here would deal a
   * fresh order every time they came Back. Absent, a per-mount seed keeps a
   * lone rendered question stable.
   */
  shuffleSeed?: number;
}

/** A shuffle seed for one fill session — mint once, pass to every Render. */
export function newShuffleSeed(): number {
  return Math.floor(Math.random() * 0x7fffffff);
}

/** Sentinel for the "Other" entry in a choice list — never a real option id. */
const OTHER = "__other__";

/**
 * A stable per-applicant shuffle.
 *
 * `shuffle` asks for a different order per applicant, not per visit — a list
 * that reorders itself as you tick it, or between Back and Next, is unusable.
 * Ordered by a seed-mixed hash of each option's id, so the order survives
 * re-renders, remounts and collaborative updates without being stored.
 */
function useApplicantOrder(
  entries: QuestionOption[],
  shuffle: boolean,
  sessionSeed: number | undefined,
): QuestionOption[] {
  const fallback = useRef<number | null>(null);
  if (fallback.current === null) fallback.current = newShuffleSeed();
  if (!shuffle) return entries;
  const mixed = sessionSeed ?? fallback.current;
  return [...entries].sort(
    (a, b) => hashWithSeed(a.id, mixed) - hashWithSeed(b.id, mixed),
  );
}

function hashWithSeed(text: string, seed: number): number {
  let hash = seed;
  for (let index = 0; index < text.length; index++) {
    hash = (Math.imul(hash, 31) + text.charCodeAt(index)) | 0;
  }
  return hash;
}

/** The message shown where a choice question has nothing to offer yet. */
function NoEntries({ children }: { children: ReactNode }) {
  return <p className="text-sm italic text-muted-foreground">{children}</p>;
}

/**
 * The way back to "unanswered" for a control that commits on first tap.
 *
 * A radio, dropdown, scale or single-pick grid can only ever *replace* its
 * answer — no tap emits nothing. On an optional question that traps the
 * applicant in an answer they may not want to give, and with `isAnswered` /
 * `isEmpty` steering routing, "answered at all" is itself an answer. Hidden on
 * required questions, where clearing could only manufacture an error.
 */
function ClearSelection({
  question,
  value,
  onChange,
}: Pick<QuestionRenderProps, "question" | "value" | "onChange">) {
  if (question.required || value === undefined) return null;
  return (
    <button
      type="button"
      className="self-start text-xs text-muted-foreground underline-offset-2 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50"
      onClick={() => onChange(undefined)}
    >
      Clear selection
    </button>
  );
}

function textValue(value: AnswerValue | undefined): string {
  return value?.kind === "text" ? value.value : "";
}

/** A typed text change as an answer — cleared text is no answer at all. */
function asText(text: string): AnswerValue | undefined {
  return text === "" ? undefined : { kind: "text", value: text };
}

function ShortAnswerRender({
  question,
  value,
  onChange,
  labelId,
  invalid,
}: QuestionRenderProps) {
  return (
    <Input
      aria-labelledby={labelId}
      aria-required={question.required || undefined}
      aria-invalid={invalid || undefined}
      className="max-w-md"
      value={textValue(value)}
      onChange={(event) => onChange(asText(event.target.value))}
      autoComplete="off"
      data-question-type={question.type}
    />
  );
}

function MoneyRender({
  question,
  value,
  onChange,
  labelId,
  invalid,
}: QuestionRenderProps) {
  return (
    <div className="flex max-w-md flex-col gap-1.5">
      <InputGroup>
        <InputGroupAddon>
          <InputGroupText id={`${labelId}-currency`}>USD</InputGroupText>
        </InputGroupAddon>
        <InputGroupInput
          aria-labelledby={labelId}
          aria-describedby={`${labelId}-currency ${labelId}-money-hint`}
          aria-required={question.required || undefined}
          aria-invalid={invalid || undefined}
          inputMode="decimal"
          placeholder="0.00"
          value={textValue(value)}
          onChange={(event) => onChange(asText(event.target.value))}
          autoComplete="off"
          data-question-type={question.type}
        />
      </InputGroup>
      <p id={`${labelId}-money-hint`} className="text-sm text-muted-foreground">
        Up to two decimal places. For example, 25,000.50.
      </p>
    </div>
  );
}

function ParagraphRender({
  question,
  value,
  onChange,
  labelId,
  invalid,
}: QuestionRenderProps) {
  return (
    <Textarea
      aria-labelledby={labelId}
      aria-required={question.required || undefined}
      aria-invalid={invalid || undefined}
      value={textValue(value)}
      onChange={(event) => onChange(asText(event.target.value))}
    />
  );
}

/** The single selected choice, if the answer holds one. */
function selectedChoice(value: AnswerValue | undefined): Choice | null {
  return value?.kind === "choice" ? value.choice : null;
}

function MultipleChoiceRender({
  question,
  value,
  onChange,
  labelId,
  invalid,
  shuffleSeed,
}: QuestionRenderProps) {
  const options = useApplicantOrder(
    optionsOf(question),
    question.config?.shuffle === true,
    shuffleSeed,
  );
  const allowOther = offersOther(question);
  if (options.length === 0 && !allowOther) {
    return <NoEntries>No options yet</NoEntries>;
  }
  const reconciled = reconcileAnswer(question, value);
  const choice = selectedChoice(reconciled);
  const selected =
    choice?.kind === "option"
      ? choice.optionId
      : choice?.kind === "other"
        ? OTHER
        : null;
  return (
    <div className="flex flex-col gap-2">
      <RadioGroup
        aria-labelledby={labelId}
        aria-required={question.required || undefined}
        aria-invalid={invalid || undefined}
        className="gap-2.5"
        value={selected}
        onValueChange={(next) => {
          if (next === OTHER) {
            onChange({ kind: "choice", choice: { kind: "other", value: "" } });
          } else if (typeof next === "string") {
            onChange({
              kind: "choice",
              choice: { kind: "option", optionId: next },
            });
          }
        }}
      >
        {options.map((option) => (
          <label key={option.id} className="flex items-center gap-2.5 text-sm">
            <RadioGroupItem value={option.id} />
            <span className="min-w-0 break-words">{option.label}</span>
          </label>
        ))}
        {allowOther ? (
          <label className="flex items-center gap-2.5 text-sm">
            <RadioGroupItem value={OTHER} />
            <span className="shrink-0 text-muted-foreground">Other:</span>
            <Input
              aria-label="Other answer"
              className="h-7 max-w-56"
              value={choice?.kind === "other" ? choice.value : ""}
              onChange={(event) =>
                onChange({
                  kind: "choice",
                  choice: { kind: "other", value: event.target.value },
                })
              }
            />
          </label>
        ) : null}
      </RadioGroup>
      <ClearSelection
        question={question}
        value={reconciled}
        onChange={onChange}
      />
    </div>
  );
}

/** The ticked choices, if the answer holds any. */
function selectedChoices(value: AnswerValue | undefined): Choice[] {
  return value?.kind === "choices" ? value.choices : [];
}

/** Ticked choices as an answer — none ticked is no answer at all. */
function asChoices(choices: Choice[]): AnswerValue | undefined {
  return choices.length === 0 ? undefined : { kind: "choices", choices };
}

function CheckboxesRender({
  question,
  value,
  onChange,
  labelId,
  invalid,
  shuffleSeed,
}: QuestionRenderProps) {
  const options = useApplicantOrder(
    optionsOf(question),
    question.config?.shuffle === true,
    shuffleSeed,
  );
  const allowOther = offersOther(question);
  if (options.length === 0 && !allowOther) {
    return <NoEntries>No options yet</NoEntries>;
  }
  // Every read and write goes through the reconciled answer, not the raw one:
  // a collaborator can delete an option that is already ticked here, and a
  // stale id carried forward would fail validation with no control left on
  // screen to untick it. Same repair the holder of the answers applies when
  // the form changes — this is the belt for callers that don't.
  const choices = selectedChoices(reconcileAnswer(question, value));
  const ticked = new Set(
    choices
      .filter((choice) => choice.kind === "option")
      .map((choice) => choice.optionId),
  );
  const other = choices.find((choice) => choice.kind === "other");
  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className="flex flex-col gap-2.5"
    >
      {options.map((option) => (
        <label key={option.id} className="flex items-center gap-2.5 text-sm">
          <Checkbox
            aria-invalid={invalid || undefined}
            checked={ticked.has(option.id)}
            onCheckedChange={(checked) => {
              const kept = choices.filter(
                (choice) =>
                  choice.kind !== "option" || choice.optionId !== option.id,
              );
              onChange(
                asChoices(
                  checked
                    ? [...kept, { kind: "option", optionId: option.id }]
                    : kept,
                ),
              );
            }}
          />
          <span className="min-w-0 break-words">{option.label}</span>
        </label>
      ))}
      {allowOther ? (
        <label className="flex items-center gap-2.5 text-sm">
          <Checkbox
            aria-invalid={invalid || undefined}
            checked={other !== undefined}
            onCheckedChange={(checked) => {
              const kept = choices.filter((choice) => choice.kind !== "other");
              onChange(
                asChoices(
                  checked ? [...kept, { kind: "other", value: "" }] : kept,
                ),
              );
            }}
          />
          <span className="shrink-0 text-muted-foreground">Other:</span>
          <Input
            aria-label="Other answer"
            className="h-7 max-w-56"
            value={other?.kind === "other" ? other.value : ""}
            onChange={(event) => {
              const kept = choices.filter((choice) => choice.kind !== "other");
              onChange(
                asChoices([
                  ...kept,
                  { kind: "other", value: event.target.value },
                ]),
              );
            }}
          />
        </label>
      ) : null}
    </div>
  );
}

function DropdownRender({
  question,
  value,
  onChange,
  labelId,
  invalid,
  shuffleSeed,
}: QuestionRenderProps) {
  const options = useApplicantOrder(
    optionsOf(question),
    question.config?.shuffle === true,
    shuffleSeed,
  );
  if (options.length === 0) return <NoEntries>No options yet</NoEntries>;
  const reconciled = reconcileAnswer(question, value);
  const choice = selectedChoice(reconciled);
  return (
    <div className="flex flex-col gap-2">
      <Select
        value={choice?.kind === "option" ? choice.optionId : undefined}
        onValueChange={(next) => {
          if (typeof next === "string") {
            onChange({
              kind: "choice",
              choice: { kind: "option", optionId: next },
            });
          }
        }}
      >
        <SelectTrigger
          aria-labelledby={labelId}
          aria-required={question.required || undefined}
          aria-invalid={invalid || undefined}
          className="w-56 max-w-full"
        >
          <SelectValue placeholder="Choose" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ClearSelection
        question={question}
        value={reconciled}
        onChange={onChange}
      />
    </div>
  );
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** `--MM-DD` (optionally `T HH:MM`) pulled apart for the month-day controls. */
function parseMonthDay(value: AnswerValue | undefined): {
  month: string;
  day: string;
  time: string;
} {
  const match = /^--(\d{2})-(\d{2})(?:T(.+))?$/.exec(textValue(value));
  return {
    month: match?.[1] ?? "",
    day: match?.[2] ?? "",
    time: match?.[3] ?? "",
  };
}

function MonthDayRender({
  question,
  value,
  onChange,
  labelId,
  invalid,
}: QuestionRenderProps) {
  const includeTime = question.config?.includeTime === true;
  const { month, day, time } = parseMonthDay(value);
  // Emitted as soon as any part is chosen: a partial pick must round-trip
  // through the answer (there is nowhere else to hold it), and validation is
  // what says "give a month *and* day" if it's still partial on continue.
  const emit = (nextMonth: string, nextDay: string, nextTime: string) => {
    if (nextMonth === "" && nextDay === "" && nextTime === "") {
      onChange(undefined);
      return;
    }
    const base = `--${nextMonth.padStart(2, "0")}-${nextDay.padStart(2, "0")}`;
    onChange(
      asText(includeTime && nextTime !== "" ? `${base}T${nextTime}` : base),
    );
  };
  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className="flex flex-wrap items-center gap-2"
    >
      <Select
        value={month === "" ? undefined : month}
        onValueChange={(next) => {
          if (typeof next === "string") emit(next, day, time);
        }}
      >
        <SelectTrigger
          aria-label="Month"
          aria-required={question.required || undefined}
          aria-invalid={invalid || undefined}
          className="w-36"
        >
          <SelectValue placeholder="Month" />
        </SelectTrigger>
        <SelectContent>
          {MONTH_NAMES.map((name, index) => (
            <SelectItem key={name} value={String(index + 1).padStart(2, "0")}>
              {name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={day === "" ? undefined : day}
        onValueChange={(next) => {
          if (typeof next === "string") emit(month, next, time);
        }}
      >
        <SelectTrigger
          aria-label="Day"
          aria-required={question.required || undefined}
          aria-invalid={invalid || undefined}
          className="w-24"
        >
          <SelectValue placeholder="Day" />
        </SelectTrigger>
        <SelectContent>
          {Array.from({ length: 31 }, (_, index) => (
            <SelectItem
              key={index + 1}
              value={String(index + 1).padStart(2, "0")}
            >
              {index + 1}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {includeTime ? (
        <Input
          type="time"
          aria-label="Time"
          aria-invalid={invalid || undefined}
          className="w-32"
          value={time}
          onChange={(event) => emit(month, day, event.target.value)}
        />
      ) : null}
      {/* The selects commit on first tap and can't be individually unset, so
          without this a partial pick on an optional question is a dead end —
          the value can never become undefined again. */}
      <ClearSelection question={question} value={value} onChange={onChange} />
    </div>
  );
}

function DateRender(props: QuestionRenderProps) {
  const { question, value, onChange, labelId, invalid } = props;
  if (question.config?.monthDayOnly === true) {
    return <MonthDayRender {...props} />;
  }
  return (
    <Input
      type={question.config?.includeTime === true ? "datetime-local" : "date"}
      aria-labelledby={labelId}
      aria-required={question.required || undefined}
      aria-invalid={invalid || undefined}
      className="w-56"
      value={textValue(value)}
      onChange={(event) => onChange(asText(event.target.value))}
    />
  );
}

function TimeRender({
  question,
  value,
  onChange,
  labelId,
  invalid,
}: QuestionRenderProps) {
  if (question.config?.duration === true) {
    return (
      <Input
        aria-labelledby={labelId}
        aria-required={question.required || undefined}
        aria-invalid={invalid || undefined}
        className="w-40"
        placeholder="hours:minutes"
        value={textValue(value)}
        onChange={(event) => onChange(asText(event.target.value))}
        autoComplete="off"
      />
    );
  }
  return (
    <Input
      type="time"
      aria-labelledby={labelId}
      aria-required={question.required || undefined}
      aria-invalid={invalid || undefined}
      className="w-32"
      value={textValue(value)}
      onChange={(event) => onChange(asText(event.target.value))}
    />
  );
}

/**
 * File uploads can't be taken yet — publishing refuses a *required* file
 * question, but an optional one can reach an applicant, who is told plainly
 * rather than shown a control that goes nowhere.
 */
/**
 * The file control. Everything about the answer is the server's: a pick
 * starts an upload through the session's registry, and the answer shown is
 * whatever the server says is attached. The control holds no upload state
 * of its own — the registry outlives it, so a hidden question or a section
 * change loses nothing. What's local here is only what a rendering owns: a
 * drag highlight, a notice, and which row's download is being fetched.
 *
 * Without a registry (the builder's preview) it draws what applicants will
 * see, wired to nothing.
 */
function FileUploadRender({
  question,
  value,
  labelId,
  invalid,
}: QuestionRenderProps) {
  const { registry } = useFileUploads();
  const max = maxFilesOf(question);
  const maxBytes = maxFileSizeMbOf(question) * 1024 * 1024;
  const acceptedTypes = question.config?.acceptedTypes;
  const reconciled = reconcileAnswer(question, value);
  const attached = reconciled?.kind === "files" ? reconciled.files : [];
  const [fetching, setFetching] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const pending = useSyncExternalStore(
    registry?.subscribe ?? noSubscribe,
    () => registry?.pendingFor(question.id) ?? NO_PENDING,
  );
  const summary = fileUploadSummary(question);

  if (!registry) {
    return (
      <div className="flex w-72 max-w-full items-center gap-2 rounded-md border border-dashed border-border-strong px-3 py-2 text-sm text-muted-foreground">
        <PaperclipIcon aria-hidden className="size-4 shrink-0" />
        <span>{summary} · answered in the published form</span>
      </div>
    );
  }

  const uploading = pending.filter(
    (item) => item.kind === "upload" && item.error === null,
  );
  const removing = new Set(
    pending
      .filter((item) => item.kind === "remove" && item.error === null)
      .map((item) => item.fileId),
  );
  const room = max - attached.length - uploading.length;

  function startUploads(files: File[]) {
    setNotice(null);
    const accepted = files.slice(0, Math.max(room, 0));
    if (files.length > accepted.length) {
      setNotice(
        room <= 0
          ? max === 1
            ? "This question takes one file — remove the current one first."
            : `This question takes up to ${max} files.`
          : `Only ${room} more ${room === 1 ? "file" : "files"} can be added here.`,
      );
    }
    for (const file of accepted) {
      // Refused locally, before any request: the applicant sees why at once.
      registry!.startUpload(question.id, file, (candidate) => {
        if (candidate.size > maxBytes) {
          return `Too large — files here can be at most ${maxFileSizeMbOf(question)} MB.`;
        }
        if (!isAcceptedType(declaredContentType(candidate), acceptedTypes)) {
          return "This question doesn't accept that kind of file.";
        }
        return null;
      });
    }
  }

  async function remove(file: FileRef) {
    setNotice(null);
    await registry!.remove(question.id, file.fileId, file.filename);
  }

  async function download(fileId: string) {
    setFetching(fileId);
    setNotice(null);
    try {
      // An attachment URL downloads in place; the page stays.
      window.location.assign(await registry!.downloadUrl(fileId));
    } catch (error) {
      setNotice(
        error instanceof FileUploadRefusal
          ? error.message
          : "The download link couldn't be fetched. Try again.",
      );
    } finally {
      setFetching(null);
    }
  }

  const accept = acceptedTypes?.length ? acceptedTypes.join(",") : undefined;
  const rowClass =
    "flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm";

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      aria-required={question.required || undefined}
      aria-invalid={invalid || undefined}
      className="flex w-full max-w-xl flex-col gap-2"
    >
      {attached.map((file) => (
        <div key={file.fileId} className={rowClass}>
          <PaperclipIcon
            aria-hidden
            className="size-4 shrink-0 text-muted-foreground"
          />
          <span className="min-w-0 flex-1 truncate">{file.filename}</span>
          {removing.has(file.fileId) ? (
            <span className="label-sm flex items-center gap-1.5 text-muted-foreground">
              <Spinner aria-hidden className="size-3.5 animate-spin" />
              Removing…
            </span>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                // A real 44px target on touch, not a 32px button wearing a
                // grown ::after. The grown-pseudo-element trick is right for a
                // lone control (the finality chip, the forms table's row
                // menu), but Download and Remove sit 8px apart, so two 6px
                // expansions overlapped by 4px — and Remove, the destructive
                // one, is the later sibling, so it won the overlap and a tap
                // aimed at Download could detach the file. Real geometry can't
                // do that: what you see is what you tap.
                className="pointer-coarse:size-11"
                aria-label={`Download ${file.filename}`}
                isLoading={fetching === file.fileId}
                disabled={fetching !== null}
                onClick={() => void download(file.fileId)}
              >
                <DownloadIcon />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="pointer-coarse:size-11"
                aria-label={`Remove ${file.filename}`}
                disabled={fetching !== null}
                onClick={() => void remove(file)}
              >
                <XIcon />
              </Button>
            </>
          )}
        </div>
      ))}

      {pending
        .filter((item) => item.kind === "upload" || item.error !== null)
        .map((item) => (
          <div
            key={item.key}
            className={cn(
              "flex flex-col gap-1.5 rounded-md border px-3 py-2 text-sm",
              item.error === null ? "border-border" : "border-destructive",
            )}
          >
            <div className="flex items-center gap-2">
              <UploadIcon
                aria-hidden
                className="size-4 shrink-0 text-muted-foreground"
              />
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
              {item.error === null ? (
                // The bar tracks bytes leaving the browser. Once they've all
                // gone the server is still checking and storing the file, and
                // "100%" over a file that isn't attached yet reads as done —
                // so that stretch says what it is.
                item.fraction >= 1 ? (
                  <span className="label-sm flex items-center gap-1.5 text-muted-foreground">
                    <Spinner aria-hidden className="size-3.5 animate-spin" />
                    Saving…
                  </span>
                ) : (
                  <span className="label-sm tabular-nums text-muted-foreground">
                    {Math.round(item.fraction * 100)}%
                  </span>
                )
              ) : null}
              {item.error === null && item.fraction >= 1 ? null : (
                // No cancel once the bytes have all left: the server will
                // finish attaching the file whatever the browser does, and a
                // cancel that only discarded the reply would leave a hidden
                // attachment. Once it appears, "remove" is the honest verb.
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="pointer-coarse:size-11"
                  aria-label={
                    item.error === null
                      ? `Cancel uploading ${item.name}`
                      : `Dismiss ${item.name}`
                  }
                  onClick={() => registry!.dismiss(item.key)}
                >
                  <XIcon />
                </Button>
              )}
            </div>
            {item.error === null ? (
              <Progress
                aria-label={
                  item.fraction >= 1
                    ? `Saving ${item.name}`
                    : `Uploading ${item.name}`
                }
                value={item.fraction * 100}
                className="h-1"
              />
            ) : (
              <p role="alert" className="body-sm text-destructive">
                {item.error}
              </p>
            )}
          </div>
        ))}

      {room > 0 ? (
        <div
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            startUploads(Array.from(event.dataTransfer.files));
          }}
          className={cn(
            "flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-dashed px-3 py-2.5 transition-colors duration-150",
            dragging ? "border-accent bg-accent/5" : "border-border-strong",
          )}
        >
          <input
            ref={inputRef}
            type="file"
            className="sr-only"
            tabIndex={-1}
            accept={accept}
            multiple={room > 1}
            onChange={(event) => {
              startUploads(Array.from(event.target.files ?? []));
              // The same file picked twice must fire again.
              event.target.value = "";
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
          >
            <UploadIcon />
            {attached.length + uploading.length > 0
              ? "Add file"
              : "Choose file"}
          </Button>
          <span className="body-sm text-muted-foreground">
            or drop {room === 1 ? "a file" : "files"} here · {summary}
          </span>
        </div>
      ) : null}

      {notice ? (
        <p role="alert" className="body-sm text-destructive">
          {notice}
        </p>
      ) : null}
    </div>
  );
}

const NO_PENDING: readonly never[] = [];
const noSubscribe = () => () => {};

/** Past this many points a scale stops being tappable and becomes a number. */
const MAX_TAPPABLE_SCALE_POINTS = 21;

function numberValue(value: AnswerValue | undefined): number | null {
  return value?.kind === "number" ? value.value : null;
}

function LinearScaleRender({
  question,
  value,
  onChange,
  labelId,
  invalid,
}: QuestionRenderProps) {
  const min = Math.round(question.config?.min ?? 1);
  const max = Math.round(question.config?.max ?? 5);
  const reconciled = reconcileAnswer(question, value);
  const selected = numberValue(reconciled);
  // A draft can say 1 to 5000; that's a draft, not a reason to draw five
  // thousand radios. Publishing enforces sane bounds — until then, a plain
  // number field keeps the question answerable.
  if (max < min || max - min + 1 > MAX_TAPPABLE_SCALE_POINTS) {
    return (
      <Input
        type="number"
        aria-labelledby={labelId}
        aria-required={question.required || undefined}
        aria-invalid={invalid || undefined}
        className="w-32"
        min={min}
        max={max}
        value={selected ?? ""}
        onChange={(event) => {
          const next = event.target.value;
          onChange(
            next === "" ? undefined : { kind: "number", value: Number(next) },
          );
        }}
      />
    );
  }
  return (
    <div className="flex flex-wrap items-end gap-1 text-sm">
      {question.config?.minLabel ? (
        <span className="mr-2 self-center text-xs text-muted-foreground">
          {question.config.minLabel}
        </span>
      ) : null}
      <RadioGroup
        aria-labelledby={labelId}
        aria-required={question.required || undefined}
        aria-invalid={invalid || undefined}
        className="flex flex-row flex-wrap items-end gap-1"
        value={selected === null ? null : String(selected)}
        onValueChange={(next) => {
          if (typeof next === "string") {
            onChange({ kind: "number", value: Number(next) });
          }
        }}
      >
        {Array.from({ length: max - min + 1 }, (_, index) => {
          const point = min + index;
          return (
            <label
              key={point}
              className="flex w-8 flex-col items-center gap-1.5"
            >
              <span className="text-muted-foreground">{point}</span>
              <RadioGroupItem value={String(point)} />
            </label>
          );
        })}
      </RadioGroup>
      {question.config?.maxLabel ? (
        <span className="ml-2 self-center text-xs text-muted-foreground">
          {question.config.maxLabel}
        </span>
      ) : null}
      <ClearSelection
        question={question}
        value={reconciled}
        onChange={onChange}
      />
    </div>
  );
}

function RatingRender({
  question,
  value,
  onChange,
  labelId,
  invalid,
}: QuestionRenderProps) {
  const max = ratingMax(question);
  const Icon = RATING_ICONS[question.config?.ratingIcon ?? "star"];
  const reconciled = reconcileAnswer(question, value);
  const selected = numberValue(reconciled) ?? 0;
  return (
    <div className="flex flex-col gap-2">
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        aria-required={question.required || undefined}
        aria-invalid={invalid || undefined}
        className="flex items-center gap-1"
      >
        {Array.from({ length: max }, (_, index) => {
          const point = index + 1;
          const filled = point <= selected;
          return (
            <button
              key={point}
              type="button"
              role="radio"
              aria-checked={point === selected}
              aria-label={`${point} of ${max}`}
              className="rounded-sm p-1 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
              onClick={() =>
                // Tapping the current rating takes it back — the only way a
                // tap-only control can offer "never mind" on an optional question.
                onChange(
                  point === selected
                    ? undefined
                    : { kind: "number", value: point },
                )
              }
            >
              <Icon
                aria-hidden
                className={cn(
                  "size-6",
                  filled
                    ? "fill-primary stroke-primary"
                    : "stroke-muted-foreground",
                )}
              />
            </button>
          );
        })}
      </div>
      {/* Re-tapping the current rating also clears; this is the visible way. */}
      <ClearSelection
        question={question}
        value={reconciled}
        onChange={onChange}
      />
    </div>
  );
}

function gridRows(value: AnswerValue | undefined): Map<string, string[]> {
  const rows = new Map<string, string[]>();
  if (value?.kind === "grid") {
    for (const row of value.rows) rows.set(row.rowId, row.optionIds);
  }
  return rows;
}

/** Answered rows as an answer — an unanswered grid is no answer at all. */
function asGrid(rows: Map<string, string[]>): AnswerValue | undefined {
  const kept = [...rows.entries()]
    .filter(([, optionIds]) => optionIds.length > 0)
    .map(([rowId, optionIds]) => ({ rowId, optionIds }));
  return kept.length === 0 ? undefined : { kind: "grid", rows: kept };
}

function GridRender({
  question,
  value,
  onChange,
  labelId,
  invalid,
  shuffleSeed,
}: QuestionRenderProps) {
  const rows = useApplicantOrder(
    rowsOf(question),
    question.config?.shuffle === true,
    shuffleSeed,
  );
  const columns = optionsOf(question);
  if (rows.length === 0 || columns.length === 0) {
    return (
      <NoEntries>
        {rows.length === 0 ? "No rows yet" : "No columns yet"}
      </NoEntries>
    );
  }
  const multi = question.config?.multiSelect === true;
  // Reconciled against the rows and columns the grid offers *now* — same
  // reasoning as the checkbox list: a collaborator can delete a row or column
  // that is already answered, and a stale id carried into the next write would
  // fail validation with nothing on screen to clear it.
  const reconciled = reconcileAnswer(question, value);
  const answered = gridRows(reconciled);
  return (
    <div className="flex flex-col gap-2 overflow-x-auto">
      <table className="text-sm" aria-labelledby={labelId}>
        <thead>
          <tr>
            <th />
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                className="px-3 pb-2 text-center font-normal text-muted-foreground"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const picked = answered.get(row.id) ?? [];
            const cells = columns.map((column) => (
              <td key={column.id} className="px-3 py-1.5 text-center">
                {multi ? (
                  <Checkbox
                    aria-label={`${row.label}: ${column.label}`}
                    aria-invalid={invalid || undefined}
                    checked={picked.includes(column.id)}
                    onCheckedChange={(checked) => {
                      const next = new Map(answered);
                      next.set(
                        row.id,
                        checked
                          ? [...picked, column.id]
                          : picked.filter((id) => id !== column.id),
                      );
                      onChange(asGrid(next));
                    }}
                  />
                ) : (
                  <RadioGroupItem
                    aria-label={`${row.label}: ${column.label}`}
                    value={column.id}
                  />
                )}
              </td>
            ));
            const header = (
              <th scope="row" className="pr-3 text-left font-normal">
                {row.label}
              </th>
            );
            return multi ? (
              <tr key={row.id}>
                {header}
                {cells}
              </tr>
            ) : (
              // One radio group per row, rendered as the row itself so the
              // table stays a table. `className` wins over the wrapper's
              // default `grid`, which would break table layout.
              <RadioGroup
                key={row.id}
                render={<tr />}
                className="table-row"
                aria-label={row.label}
                aria-invalid={invalid || undefined}
                value={picked[0] ?? null}
                onValueChange={(next) => {
                  if (typeof next !== "string") return;
                  const map = new Map(answered);
                  map.set(row.id, [next]);
                  onChange(asGrid(map));
                }}
              >
                {header}
                {cells}
              </RadioGroup>
            );
          })}
        </tbody>
      </table>
      {/* A single-pick row can only be re-picked, never un-picked, so the
          optional grid needs the same way back the other radios get. Ticks in
          a multi-select grid untick themselves. */}
      {multi ? null : (
        <ClearSelection
          question={question}
          value={reconciled}
          onChange={onChange}
        />
      )}
    </div>
  );
}

/** A text block asks nothing — its label and help are the whole content. */
function TextBlockRender(_props: QuestionRenderProps) {
  return null;
}

export const QUESTION_TYPE_RENDER: Record<
  QuestionType,
  ComponentType<QuestionRenderProps>
> = {
  short_answer: ShortAnswerRender,
  money: MoneyRender,
  paragraph: ParagraphRender,
  multiple_choice: MultipleChoiceRender,
  checkboxes: CheckboxesRender,
  dropdown: DropdownRender,
  date: DateRender,
  time: TimeRender,
  file_upload: FileUploadRender,
  linear_scale: LinearScaleRender,
  rating: RatingRender,
  grid: GridRender,
  text_block: TextBlockRender,
};

export function renderFor(
  type: QuestionType,
): ComponentType<QuestionRenderProps> {
  return QUESTION_TYPE_RENDER[type];
}
